import {
  AHButton,
  AHSeparator,
  AHText,
  Box,
  Card,
  Circle,
  ListItem,
  SectionHeader,
  SelectDot,
  TouchableBox,
  useLayoutContext,
} from "arkh";
import { DateTime } from "luxon";

import React, { useCallback } from "react";
import { FormattedMessage } from "react-intl";
import { FlatList, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useLazyLoadQuery, graphql, useMutation } from "react-relay";
import { useWeekdays } from "../../common/useWeekdays";
import { LazyQueryNetworkBoundrary } from "../../components/mobile/QueryNetworkBoundrary";
import { useIntlTokens } from "../../components/mobile/useIntlTokens";
import { TimePicker } from "../RoutineAdmin/RoutineEditScheduleDailyTaskImportance/TimePicker";
import { StorecastScreenAdminAddScheduleItemMutation } from "./__generated__/StorecastScreenAdminAddScheduleItemMutation.graphql";
import { StorecastScreenAdminAddScheduleItemQuery } from "./__generated__/StorecastScreenAdminAddScheduleItemQuery.graphql";
import { useAlert } from "../../common/Alert";
import { StoreAdminNavigationScreenProps } from "../StoreAdmin/StoreAdminNavigation";

export function StorecastScreenAdminAddScheduleItemScreen(
  props: StoreAdminNavigationScreenProps<"AddOrEditScheduleItem">
) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => (
        <StorecastScreenAdminAddScheduleItem
          key={props.route.params.scheduleId + props.route.params.screen.id}
          screenId={props.route.params.screen.id}
          fetchKey={fetchKey}
          onDone={() => props.navigation.goBack()}
          previousScheduleItemId={props.route.params.scheduleId}
        />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

function StorecastScreenAdminAddScheduleItem(props: {
  screenId: string;
  fetchKey: string;
  previousScheduleItemId?: string;
  onDone: () => void;
}) {
  const data = useLazyLoadQuery<StorecastScreenAdminAddScheduleItemQuery>(
    graphql`
      query StorecastScreenAdminAddScheduleItemQuery($screenId: ID!) {
        store {
          dashboards {
            id
            name
          }
        }
        storecast {
          screen(id: $screenId) {
            scheduledStorecasts {
              id
              startAt
              endAt
              storecast {
                id
              }
              weekdays
            }
            id
          }
        }
      }
    `,
    { screenId: props.screenId },
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  const intl_tokens = useIntlTokens();
  const previousItem = props.previousScheduleItemId
    ? data.storecast.screen.scheduledStorecasts.find(
        (item) => item.id === props.previousScheduleItemId
      )
    : undefined;

  const [selectedDashboard, setSelectedDashboard] = React.useState<
    string | null
  >(previousItem?.storecast?.id ?? null);

  const [selectedDays, setSelectedDays] = React.useState<number[]>(
    previousItem?.weekdays.map((wd) => dayToDayIndex(wd)) ?? []
  );
  const [startAt, setStartAt] = React.useState(
    DateTime.fromISO(previousItem?.startAt ?? "08:00").toJSDate()
  );
  const [endAt, setEndAt] = React.useState(
    DateTime.fromISO(previousItem?.endAt ?? "20:00").toJSDate()
  );
  const insets = useSafeAreaInsets();

  const [mutate, isSaving] =
    useMutation<StorecastScreenAdminAddScheduleItemMutation>(graphql`
      mutation StorecastScreenAdminAddScheduleItemMutation(
        $screenId: ID!
        $items: [StorecastDeviceSetDashboardScheduleInputItem!]
        $deleteItems: [ID!]
      ) {
        storecast {
          setScreenDashboardSchedule(
            addItems: $items
            deleteItems: $deleteItems
            screenId: $screenId
          ) {
            id
            ...StorecastScreenAdminSchedule_StorecastScreen
          }
        }
      }
    `);
  const Alert = useAlert();

  const deleteItem = () => {
    if (!isSaving && props.previousScheduleItemId) {
      mutate({
        onError: (e) => {
          Alert.error("Error", e.message);
        },
        onCompleted: () => {
          props.onDone();
        },

        variables: {
          deleteItems: [props.previousScheduleItemId],
          screenId: data.storecast.screen.id,
        },
      });
    }
  };

  const save = () => {
    if (!isSaving && selectedDashboard) {
      mutate({
        onError: (e) => {
          Alert.error("Error", e.message);
        },
        onCompleted: () => {
          props.onDone();
        },

        variables: {
          deleteItems: previousItem ? [previousItem.id] : [],
          items: [
            {
              weekdays: selectedDays.map((day) => indexToDay(day)),
              startAt: DateTime.fromJSDate(startAt).toISOTime({
                includeOffset: false,
                includePrefix: false,
                suppressSeconds: true,
              })!,
              endAt: DateTime.fromJSDate(endAt).toISOTime({
                includeOffset: false,
                includePrefix: false,
                suppressSeconds: true,
              })!,
              storecastId: selectedDashboard,
            },
          ],
          screenId: data.storecast.screen.id,
        },
      });
    }
  };
  const layout = useLayoutContext();
  return (
    <>
      <FlatList
        ListHeaderComponent={
          <>
            <SectionHeader>
              <SectionHeader.Title>
                <FormattedMessage
                  description="ScreenAdmin schedule storecast time title"
                  defaultMessage="Schedule"
                />
              </SectionHeader.Title>
            </SectionHeader>
            <Card noSpace>
              <ListItem
                right={
                  <TimePicker
                    date={startAt}
                    maxDate={endAt}
                    setDate={(_, date) => {
                      if (date) {
                        setStartAt(date);
                      }
                    }}
                  />
                }
              >
                <ListItem.Title>
                  <FormattedMessage
                    description="ScreenAdmin schedule storecast start time label"
                    defaultMessage="Start"
                  />
                </ListItem.Title>
              </ListItem>
              <AHSeparator />
              <ListItem
                right={
                  <TimePicker
                    date={endAt}
                    minDate={startAt}
                    setDate={(_, date) => {
                      if (date) {
                        setEndAt(date);
                      }
                    }}
                  />
                }
              >
                <ListItem.Title>
                  <FormattedMessage
                    description="ScreenAdmin schedule storecast end time label"
                    defaultMessage="End"
                  />
                </ListItem.Title>
              </ListItem>
            </Card>
            <Box spaceVertical="medium">
              <WeekdaySelector
                setWeekdays={setSelectedDays}
                weekdays={selectedDays}
              />
            </Box>
            <SectionHeader>
              <SectionHeader.Title>Storecast</SectionHeader.Title>
            </SectionHeader>
          </>
        }
        data={data.store.dashboards}
        style={{ flex: 1 }}
        contentInset={{
          bottom: insets.bottom,
        }}
        contentContainerStyle={{
          paddingVertical: layout.space.medium,
          paddingHorizontal: layout.space.medium,
        }}
        ItemSeparatorComponent={AHSeparator}
        renderItem={({ item, index }) => {
          return (
            <ListItem
              isFirst={index === 0}
              isLast={index === data.store.dashboards.length - 1}
              onPress={() => {
                setSelectedDashboard(item.id);
              }}
              right={
                <SelectDot
                  selectedDotColor="primary"
                  selected={selectedDashboard === item.id}
                />
              }
            >
              <ListItem.Title>{item.name}</ListItem.Title>
            </ListItem>
          );
        }}
      />
      <View style={{ left: 0, right: 0, bottom: 0, width: "100%" }}>
        <Box
          space="medium"
          style={{ marginBottom: insets.bottom, flexDirection: "row" }}
        >
          {props.previousScheduleItemId ? (
            <>
              <AHButton
                style={{ flex: 1 }}
                themeType="negative"
                title={intl_tokens.Delete}
                loading={isSaving}
                onPress={() => {
                  deleteItem();
                }}
              />
              <AHSeparator spaceRight="medium" />
            </>
          ) : null}
          <AHButton
            style={{ flex: 1 }}
            themeType="primary"
            loading={isSaving}
            disabled={!selectedDashboard}
            onPress={() => {
              save();
            }}
            title={intl_tokens.Save}
          />
        </Box>
      </View>
    </>
  );
}

function WeekdaySelector(props: {
  weekdays: number[];
  setWeekdays(weekdays: number[]): void;
}) {
  const weekdays = useWeekdays("narrow");

  const renderWeekday = useCallback(
    function (wd: string, index: number) {
      const isSelected =
        props.weekdays.includes(index) || props.weekdays.length === 0;
      return (
        <TouchableBox
          key={index}
          style={{ flex: 1, alignItems: "center" }}
          onPress={() => {
            if (props.weekdays.includes(index)) {
              props.setWeekdays(props.weekdays.filter((wd) => wd !== index));
            } else {
              props.setWeekdays([...props.weekdays, index]);
            }
          }}
        >
          <Circle size="medium" color={isSelected ? "primary" : "background"}>
            <AHText
              type="subtitle"
              color={isSelected ? "lightText" : "darkerText"}
            >
              {wd}
            </AHText>
          </Circle>
        </TouchableBox>
      );
    },
    [props]
  );

  const intl_tokens = useIntlTokens();

  return (
    <Box>
      <SectionHeader>
        <SectionHeader.Title>
          {intl_tokens.Weekdays_including_weekend}
        </SectionHeader.Title>
      </SectionHeader>
      <Card>
        <Box style={{ flexDirection: "row", justifyContent: "space-between" }}>
          {weekdays.map(renderWeekday)}
        </Box>
      </Card>
    </Box>
  );
}

function indexToDay(dayIndex: number) {
  switch (dayIndex) {
    case 0:
      return "MONDAY";
    case 1:
      return "TUESDAY";
    case 2:
      return "WEDNESDAY";
    case 3:
      return "THURSDAY";
    case 4:
      return "FRIDAY";
    case 5:
      return "SATURDAY";
    case 6:
      return "SUNDAY";
  }
  throw Error("Index out of bounds");
}

function dayToDayIndex(
  day:
    | "MONDAY"
    | "TUESDAY"
    | "WEDNESDAY"
    | "THURSDAY"
    | "FRIDAY"
    | "SATURDAY"
    | "SUNDAY"
) {
  switch (day) {
    case "MONDAY":
      return 0;
    case "TUESDAY":
      return 1;
    case "WEDNESDAY":
      return 2;
    case "THURSDAY":
      return 3;
    case "FRIDAY":
      return 4;
    case "SATURDAY":
      return 5;
    case "SUNDAY":
      return 6;
  }
}
